import i18n from "i18next";
/**
 * Pure helpers for the DocumentStamp truncation rule and label generation.
 *
 * Separated from the React component so the truncation rule can be unit-
 * tested without rendering. Anything visual stays in the .tsx; this file
 * is just data → label.
 */

import type { ThemeDescriptor, ThemeId } from "@/lib/api";
import {
  DEFAULT_OPTIONS_FOR_CATEGORY,
  type ClassOptionsValue,
  type DocumentCategory,
  type DocumentClass,
} from "@/types/documentClass";

/**
 * The minimal slice of `Document` the stamp needs. We accept a structural
 * subset rather than the full `Document` so the helpers stay decoupled
 * from the api type and easy to test.
 */
export interface StampDocumentInput {
  latexDocumentClass?: string | null;
  documentCategory?: DocumentCategory | null;
  paperSize?: string | null;
  fontSize?: number | null;
  columns?: number | null;
  sides?: "one" | "two" | null;
  titlePage?: boolean | null;
  orientation?: "portrait" | "landscape" | null;
}

/** Map a class string to its category (for default lookups). */
const CLASS_TO_CATEGORY: Record<string, DocumentCategory> = {
  article: "article",
  amsart: "article",
  IEEEtran: "article",
  acmart: "article",
  exam: "article",
  book: "book",
  "tufte-book": "book",
  report: "report",
  memoir: "report",
  moderncv: "cv",
  altacv: "cv",
  resume: "cv",
};

/** Pretty label for the category portion of the stamp ("Article" not "article"). */
const CATEGORY_LABEL: Record<DocumentCategory, string> = {
  article: i18n.t("editor:stamp.article"),
  book: i18n.t("editor:stamp.book"),
  report: i18n.t("editor:stamp.report"),
  cv: "CV",
};

/** A token in the rendered stamp — the stamp is the joined sequence with `·` separators. */
export interface StampToken {
  /** Stable key for React. */
  key: string;
  /** Text shown for this token. */
  text: string;
  /** True for the class token (the only semibold one). */
  isClass?: boolean;
}

/**
 * Resolve the document's category from either an explicit field or the
 * class string. Falls back to "article" so the stamp always renders.
 */
export function resolveCategory(doc: StampDocumentInput): DocumentCategory {
  if (doc.documentCategory) return doc.documentCategory;
  const klass = doc.latexDocumentClass ?? "article";
  return CLASS_TO_CATEGORY[klass] ?? "article";
}

/**
 * The document-type label alone — "Article" / "Book" / "Report" / "CV".
 * This is all the stamp surfaces now; the full class/layout meta strip
 * moved into the popover.
 */
export function computeTypeLabel(doc: StampDocumentInput): string {
  return CATEGORY_LABEL[resolveCategory(doc)];
}

/**
 * A theme's name for the stamp and Look's header: the descriptor's, or the id
 * capitalised while the list loads ("carnet" → "Carnet").
 */
export function themeLabel(id: ThemeId, themes: readonly Pick<ThemeDescriptor, "id" | "name">[]): string {
  return themes.find((t) => t.id === id)?.name ?? id.charAt(0).toUpperCase() + id.slice(1);
}

/**
 * Resolve the effective ClassOptionsValue for a document — fields present
 * on the doc win; missing fields fall back to the category default. This
 * keeps the popover usable even before Team 1/2 ship the full server
 * fields.
 */
export function resolveOptions(doc: StampDocumentInput): ClassOptionsValue {
  const category = resolveCategory(doc);
  const defaults = DEFAULT_OPTIONS_FOR_CATEGORY[category];
  return {
    paperSize: normalizePaperSize(doc.paperSize) ?? defaults.paperSize,
    fontSize: normalizeFontSize(doc.fontSize) ?? defaults.fontSize,
    columns: normalizeColumns(doc.columns) ?? defaults.columns,
    balancedColumns: defaults.balancedColumns,
    sides: doc.sides ?? defaults.sides,
    titlePage: doc.titlePage ?? defaults.titlePage,
    orientation: doc.orientation ?? defaults.orientation,
  };
}

function normalizePaperSize(v: string | null | undefined): ClassOptionsValue["paperSize"] | null {
  if (!v) return null;
  const lower = v.toLowerCase();
  if (lower === "a4" || lower === "letter" || lower === "a5") return lower;
  return null;
}

function normalizeFontSize(v: number | null | undefined): ClassOptionsValue["fontSize"] | null {
  if (v === 10 || v === 11 || v === 12) return v;
  return null;
}

function normalizeColumns(v: number | null | undefined): ClassOptionsValue["columns"] | null {
  if (v === 1 || v === 2) return v;
  return null;
}

/** Pretty-print paper size for the stamp ("A4", not "a4"). */
function paperSizeLabel(p: ClassOptionsValue["paperSize"]): string {
  if (p === "a4") return "A4";
  if (p === "a5") return "A5";
  return i18n.t("editor:stamp.letter");
}

/**
 * Compute the tokens shown in the stamp, applying the truncation rule:
 *
 * Always: class label, font size, paper size.
 *
 * Conditional:
 * - Columns:      shown only when ≠ 1
 * - Sides:        shown only when ≠ category default
 *                 (article: hide, book/report: show "2-sided" if non-default)
 * - Title page:   shown only when ≠ category default
 * - Orientation:  shown only when landscape
 */
export function computeStampTokens(doc: StampDocumentInput): StampToken[] {
  const category = resolveCategory(doc);
  const opts = resolveOptions(doc);
  const defaults = DEFAULT_OPTIONS_FOR_CATEGORY[category];

  const tokens: StampToken[] = [];

  // 1. Class — always shown, semibold.
  tokens.push({ key: "class", text: CATEGORY_LABEL[category], isClass: true });

  // 2. Font size — always shown.
  tokens.push({ key: "fontSize", text: `${opts.fontSize}pt` });

  // 3. Paper size — always shown.
  tokens.push({ key: "paperSize", text: paperSizeLabel(opts.paperSize) });

  // 4. Columns — always shown as compact `col-<N>` so the stamp
  // reads consistently regardless of single vs multi-column (user
  // direction 2026-05-13: "col-1 and col-2"). Saves chrome width
  // vs the previous "2 columns" verbose form.
  tokens.push({
    key: "columns",
    text: `col-${opts.columns}`,
  });

  // 5. Sides — only when ≠ category default.
  if (opts.sides !== defaults.sides) {
    tokens.push({
      key: "sides",
      text: opts.sides === "two" ? "2-sided" : "1-sided",
    });
  }

  // 6. Title page — only when ≠ category default.
  if (opts.titlePage !== defaults.titlePage) {
    tokens.push({
      key: "titlePage",
      text: opts.titlePage ? "title page" : "no title page",
    });
  }

  // 7. Orientation — only when landscape.
  if (opts.orientation === "landscape") {
    tokens.push({ key: "orientation", text: "landscape" });
  }

  return tokens;
}

/** Map a `DocumentClass` value to its monospace label for the popover. */
export function classMonoLabel(klass: DocumentClass | null | undefined): string {
  return klass ?? "article";
}
